import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeedPost = ({ post }) => {
  const [isLiked, setIsLiked] = useState(post?.isLiked || false);
  const [likeCount, setLikeCount] = useState(post?.likes || 0);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const navigate = useNavigate();

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const handleComment = () => {
    if (newComment?.trim()) {
      console.log('Adding comment:', newComment);
      setNewComment('');
    }
  };

  const handleShare = () => {
    console.log('Sharing post:', post?.id);
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const postTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - postTime) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-gentle">
      {/* Post Header */}
      <div className="flex items-start space-x-3 mb-4">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
          <Image
            src={post?.author?.avatar}
            alt={post?.author?.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-card-foreground truncate">
              {post?.author?.name}
            </h3>
            {post?.author?.verified && (
              <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {formatTimeAgo(post?.timestamp)}
          </p>
        </div>
        <Button variant="ghost" size="sm">
          <Icon name="MoreHorizontal" size={16} />
        </Button>
      </div>
      {/* Post Content */}
      <div className="mb-4">
        <p className="text-card-foreground mb-3 leading-relaxed">
          {post?.content}
        </p>
        
        {/* Donation Info */}
        {post?.donation && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={post?.donation?.charity?.logo}
                    alt={post?.donation?.charity?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium text-card-foreground">
                    ${post?.donation?.amount?.toLocaleString()} donated
                  </p>
                  <button
                    onClick={() => navigate('/charity-profile')}
                    className="text-sm text-primary hover:underline"
                  >
                    {post?.donation?.charity?.name}
                  </button>
                </div>
              </div>
              <Icon name="Heart" size={20} className="text-primary" />
            </div>
          </div>
        )}

        {/* Challenge Info */}
        {post?.challenge && (
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-3">
            <div className="flex items-center space-x-3">
              <Icon name="Trophy" size={20} className="text-accent" />
              <div>
                <p className="font-medium text-card-foreground">
                  Joined {post?.challenge?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  Progress: {post?.challenge?.progress}%
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Post Image */}
        {post?.image && (
          <div className="rounded-lg overflow-hidden bg-muted">
            <Image
              src={post?.image}
              alt="Post image"
              className="w-full h-64 object-cover"
            />
          </div>
        )}
      </div>
      {/* Post Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center space-x-6">
          <button
            onClick={handleLike}
            className={`flex items-center space-x-2 transition-micro hover-scale ${
              isLiked ? 'text-error' : 'text-muted-foreground hover:text-error'
            }`}
          >
            <Icon name={isLiked ? "Heart" : "Heart"} size={18} fill={isLiked ? "currentColor" : "none"} />
            <span className="text-sm font-medium">{likeCount}</span>
          </button>
          
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-micro hover-scale"
          >
            <Icon name="MessageCircle" size={18} />
            <span className="text-sm font-medium">{post?.comments?.length || 0}</span>
          </button>
          
          <button
            onClick={handleShare}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-micro hover-scale"
          >
            <Icon name="Share2" size={18} />
            <span className="text-sm font-medium">Share</span>
          </button>
        </div>
        
        <Button variant="outline" size="sm" onClick={() => navigate('/charity-profile')}>
          Donate Too
        </Button>
      </div>
      {/* Comments Section */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-border">
          {/* Add Comment */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
              JD
            </div>
            <div className="flex-1 flex space-x-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e?.target?.value)}
                placeholder="Add a comment..."
                className="flex-1 px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                onKeyPress={(e) => e?.key === 'Enter' && handleComment()}
              />
              <Button size="sm" onClick={handleComment} disabled={!newComment?.trim()}>
                Post
              </Button>
            </div>
          </div>

          {/* Comments List */}
          {post?.comments && post?.comments?.length > 0 && (
            <div className="space-y-3">
              {post?.comments?.slice(0, 3)?.map((comment) => (
                <div key={comment?.id} className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
                    <Image
                      src={comment?.author?.avatar}
                      alt={comment?.author?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="bg-muted rounded-lg px-3 py-2">
                      <p className="font-medium text-sm text-card-foreground">
                        {comment?.author?.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {comment?.content}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTimeAgo(comment?.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              
              {post?.comments?.length > 3 && (
                <button className="text-sm text-primary hover:underline">
                  View all {post?.comments?.length} comments
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedPost;